
import React from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent } from "@/components/ui/card";
import { useSecureAdmin } from "@/hooks/useSecureAdmin";
import { useEntryEditor } from "@/hooks/useEntryEditor";
import { useAutosave } from "@/hooks/useAutosave";
import { EntryEditorHeader } from "@/components/EntryEditor/EntryEditorHeader";
import { EntryForm } from "@/components/EntryEditor/EntryForm";
import { EntryStatusCard } from "@/components/EntryEditor/EntryStatusCard";
import { PasswordProtectionCard } from "@/components/EntryEditor/PasswordProtectionCard";
import { sanitizeHtml } from "@/utils/sanitization";

const EntryEditor = () => {
  const { user } = useAuth();
  const { isAdmin, loading: adminLoading } = useSecureAdmin();
  
  const {
    entry,
    setEntry,
    loading,
    saving,
    isNewEntry,
    handleSave,
    handleDelete,
    handleFieldChange,
    navigate
  } = useEntryEditor();

  const { lastSaved, autoSaving, markContentChanged } = useAutosave(
    entry,
    isNewEntry,
    loading,
    user?.id
  );

  const handleContentChange = (newContent: string) => {
    const sanitizedContent = sanitizeHtml(newContent);
    setEntry(prev => ({ ...prev, content: sanitizedContent }));
    markContentChanged();
  };

  if (loading || adminLoading) {
    return (
      <div>
        <div className="flex items-center mb-6">
          <Skeleton className="h-8 w-8 mr-2" />
          <Skeleton className="h-8 w-64" />
        </div>
        <Card>
          <CardContent className="p-6">
            <Skeleton className="h-8 w-full mb-4" />
            <Skeleton className="h-4 w-3/4 mb-2" />
            <Skeleton className="h-4 w-full mb-2" />
            <Skeleton className="h-4 w-1/2 mb-8" />
            <Skeleton className="h-48 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <EntryEditorHeader
        isNewEntry={isNewEntry}
        saving={saving}
        autoSaving={autoSaving}
        lastSaved={lastSaved}
        onSave={handleSave}
        onDelete={handleDelete}
        onBack={() => navigate(-1)}
      />

      <EntryForm
        entry={entry}
        isNewEntry={isNewEntry}
        userId={user?.id}
        onFieldChange={handleFieldChange}
        onContentChange={handleContentChange}
        markContentChanged={markContentChanged}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
        <EntryStatusCard
          entry={entry}
          isAdmin={isAdmin}
          onFieldChange={handleFieldChange}
        />
        
        <PasswordProtectionCard
          entry={entry}
          onFieldChange={handleFieldChange}
        />
      </div>
    </div>
  );
};

export default EntryEditor;
